import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_LABORATORIES_LOAD,
    ACTION_LABORATORIES_LOAD_BY_ID,
    ACTION_LABORATORIES_LOAD_FAILURE,
    ACTION_LABORATORIES_LOAD_SUCCESS,

    ACTION_LABORATORIES_SAVE,
    ACTION_LABORATORIES_SAVE_SUCCESS,
    ACTION_LABORATORIES_SAVE_FAILURE,

    ACTION_LABORATORIES_CREATE,
    ACTION_LABORATORIES_CREATE_SUCCESS,
    ACTION_LABORATORIES_CREATE_FAILURE,

    ACTION_LABORATORIES_VALIDATE,
    ACTION_LABORATORIES_VALIDATE_SUCCESS,
    ACTION_LABORATORIES_VALIDATE_FAILURE,

    ACTION_LABORATORIES_CLEAR,
    ACTION_LABORATORIES_FILTER,

    LaboratoryAction
} from '../actions/laboratories';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ILaboratoryState {
    formData: Dtos.Laboratory | Dtos.Laboratory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Laboratory[];
    formLength: number;
}

const initialState: ILaboratoryState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ILaboratoryState, _isLoading: boolean): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ILaboratoryState,
    formData: Dtos.Laboratory | Dtos.Laboratory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ILaboratoryState, responseStatus: Dtos.ResponseStatus): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ILaboratoryState, formFilter: IFormFilter): ILaboratoryState {

    const items: Dtos.Laboratory[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ILaboratoryState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ILaboratoryState, propertiesToClear?: string[]): ILaboratoryState {

    if (!propertiesToClear) {
        const newState: ILaboratoryState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ILaboratoryState, _isSaving: boolean): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ILaboratoryState,
    formData: Dtos.Laboratory | Dtos.Laboratory[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ILaboratoryState, responseStatus: Dtos.ResponseStatus): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ILaboratoryState, _isCreating: boolean): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ILaboratoryState,
    formData: Dtos.Laboratory,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ILaboratoryState, responseStatus: Dtos.ResponseStatus): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ILaboratoryState, _isValidating: boolean): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ILaboratoryState,
    formData: Dtos.Laboratory | Dtos.Laboratory[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ILaboratoryState, responseStatus: Dtos.ResponseStatus): ILaboratoryState {
    const newState: ILaboratoryState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const LaboratoryReducer: Reducer<ILaboratoryState> = (state: ILaboratoryState = initialState, action: LaboratoryAction) => {
    switch (action.type) {
        case ACTION_LABORATORIES_LOAD:
        case ACTION_LABORATORIES_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_LABORATORIES_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_LABORATORIES_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_LABORATORIES_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_LABORATORIES_SAVE:
            return saving(state, true);
        case ACTION_LABORATORIES_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORIES_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_LABORATORIES_CREATE:
            return creating(state, true);
        case ACTION_LABORATORIES_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_LABORATORIES_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_LABORATORIES_VALIDATE:
            return validating(state, true);
        case ACTION_LABORATORIES_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORIES_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_LABORATORIES_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default LaboratoryReducer;